import { isBrowser } from "@utils/isBrowser";

export const COOKIE_CONSENT_STORAGE_KEY = "n5-cookie-consent";
export const COOKIE_CONSENT_EVENT = "n5:cookie-consent-change";
export const COOKIE_PREFERENCES_EVENT = "n5:cookie-preferences-open";
export const COOKIE_CONSENT_TTL_DAYS = 180;

const GTM_CONTAINER_ID = "GTM-T26BF72";
const GTM_SCRIPT_ID = "n5-gtm-script";

const DEFAULT_CONSENT = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
  version: 1,
  updatedAt: null,
};

const ANALYTICS_COOKIE_PREFIXES = [
  "_ga",
  "_gid",
  "_gat",
  "_gcl",
  "_hj",
  "__utm",
];

const FUNCTIONAL_COOKIE_PREFIXES = [
  "jv_",
  "jivo_",
];

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const getRootDomainCandidates = () => {
  if (!isBrowser()) {
    return [];
  }

  const hostnameParts = window.location.hostname.split(".");

  if (hostnameParts.length <= 1) {
    return [window.location.hostname];
  }

  return hostnameParts.reduce((domains, _, index) => {
    const candidate = hostnameParts.slice(index).join(".");

    if (candidate.includes(".")) {
      domains.push(candidate);
    }

    return domains;
  }, [window.location.hostname]);
};

const clearCookie = (cookieName, domain) => {
  const expires = "Thu, 01 Jan 1970 00:00:00 GMT";
  const base = `${cookieName}=; expires=${expires}; path=/; SameSite=Lax`;
  document.cookie = base;

  if (domain) {
    document.cookie = `${base}; domain=${domain}`;
    document.cookie = `${base}; domain=.${domain}`;
  }
};

const clearCookiesByPrefixes = prefixes => {
  if (!isBrowser()) {
    return;
  }

  const cookies = document.cookie ? document.cookie.split(";") : [];
  const domains = getRootDomainCandidates();

  cookies.forEach(entry => {
    const [rawName] = entry.split("=");
    const cookieName = rawName ? rawName.trim() : "";
    const shouldClear = prefixes.some(prefix => cookieName.startsWith(prefix));

    if (!shouldClear) {
      return;
    }

    clearCookie(cookieName);
    domains.forEach(domain => clearCookie(cookieName, domain));
  });
};

const normalizeConsent = value => ({
  ...DEFAULT_CONSENT,
  ...(value || {}),
  necessary: true,
});

const isExpired = consent => {
  if (!consent?.updatedAt) {
    return true;
  }

  const savedAt = new Date(consent.updatedAt).getTime();

  if (Number.isNaN(savedAt)) {
    return true;
  }

  return Date.now() - savedAt > COOKIE_CONSENT_TTL_DAYS * DAY_IN_MS;
};

export const getDefaultConsent = () => ({
  ...DEFAULT_CONSENT,
});

export const getStoredConsent = () => {
  if (!isBrowser()) {
    return null;
  }

  try {
    const rawConsent = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);

    if (!rawConsent) {
      return null;
    }

    const parsedConsent = normalizeConsent(JSON.parse(rawConsent));

    if (isExpired(parsedConsent)) {
      window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
      return null;
    }

    return parsedConsent;
  } catch (error) {
    window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    return null;
  }
};

export const hasConsentFor = category => {
  const consent = getStoredConsent();

  if (!consent) {
    return false;
  }

  return Boolean(consent[category]);
};

export const saveConsent = consent => {
  if (!isBrowser()) {
    return normalizeConsent(consent);
  }

  const normalizedConsent = normalizeConsent({
    ...consent,
    updatedAt: new Date().toISOString(),
  });

  window.localStorage.setItem(
    COOKIE_CONSENT_STORAGE_KEY,
    JSON.stringify(normalizedConsent)
  );

  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_EVENT, {
      detail: normalizedConsent,
    })
  );

  return normalizedConsent;
};

export const openCookiePreferences = () => {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(new CustomEvent(COOKIE_PREFERENCES_EVENT));
};

export const loadGoogleTagManager = () => {
  if (!isBrowser() || document.getElementById(GTM_SCRIPT_ID)) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  document.head.appendChild(script);
};

export const removeGoogleTagManager = () => {
  if (!isBrowser()) {
    return;
  }

  const gtmScript = document.getElementById(GTM_SCRIPT_ID);

  if (gtmScript) {
    gtmScript.remove();
  }

  clearCookiesByPrefixes(ANALYTICS_COOKIE_PREFIXES);
};

export const cleanupChatIntegrations = () => {
  if (!isBrowser()) {
    return;
  }

  const selectors = [
    'script[src*="jivosite.com"]',
    'iframe[src*="jivosite.com"]',
    '[id^="jivo"]',
    '[class*="jivo"]',
    "jdiv",
  ];

  selectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(node => node.remove());
  });

  if (window.jivo_api) {
    delete window.jivo_api;
  }

  clearCookiesByPrefixes(FUNCTIONAL_COOKIE_PREFIXES);
};

export const trackPageView = path => {
  if (!isBrowser() || !hasConsentFor("analytics")) {
    return;
  }

  loadGoogleTagManager();

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "gatsby-route-change",
    pagePath: path,
    pageTitle: document.title,
  });
};

export const applyConsentSideEffects = (
  previousConsent = getDefaultConsent(),
  nextConsent = getDefaultConsent()
) => {
  if (!isBrowser()) {
    return;
  }

  if (!previousConsent.analytics && nextConsent.analytics) {
    loadGoogleTagManager();
    trackPageView(
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    );
  }

  if (previousConsent.analytics && !nextConsent.analytics) {
    removeGoogleTagManager();
  }

  if (previousConsent.functional && !nextConsent.functional) {
    cleanupChatIntegrations();
  }
};

export const applyStoredConsent = () => {
  const storedConsent = getStoredConsent();

  if (!storedConsent) {
    removeGoogleTagManager();
    cleanupChatIntegrations();
    return null;
  }

  applyConsentSideEffects(getDefaultConsent(), storedConsent);
  return storedConsent;
};
